/**
 * Cookie utilities for B2M Tracker
 * Used to read marketing pixel cookies (_fbp, _fbc, _ga) and persist
 * SDK-owned values (b2m_gclid, b2m_fbc) across sessions.
 */

export function getCookie(name) {
  if (typeof document === 'undefined' || !document.cookie) return null;
  try {
    const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const match = document.cookie.match(new RegExp('(?:^|;\\s*)' + escaped + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch (e) {
    return null;
  }
}

/**
 * Write a cookie.
 * @param {string} name
 * @param {string} value
 * @param {number|null} days  Lifetime in days. Pass null for a session cookie
 *                            (cleared when the browser closes).
 * @param {string|null} domain  e.g. ".birfatura.com" to share the cookie across
 *                               all subdomains. Omit for a host-only cookie.
 */
export function setCookie(name, value, days = 90, domain = null) {
  if (typeof document === 'undefined') return;
  try {
    let cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Lax`;
    if (days != null) {
      cookie += `; expires=${new Date(Date.now() + days * 864e5).toUTCString()}`;
    }
    if (domain) cookie += `; domain=${domain}`;
    // Secure is harmless on https; skip on http (localhost/dev) where the
    // browser would otherwise drop a Secure cookie.
    if (typeof location !== 'undefined' && location.protocol === 'https:') cookie += '; Secure';
    document.cookie = cookie;
  } catch (e) {
    /* ignore — third-party iframe contexts may block document.cookie writes */
  }
}

/**
 * Resolve a cookieDomain config value into an actual cookie domain (or null = off).
 *  - null / false / '' / 'off' / 'false' / 'none' → null (host-only, no sharing)
 *  - an explicit value like '.birfatura.com'        → used as-is
 *  - 'auto'                                          → derive the registrable
 *    domain (eTLD+1) by probing: try a test cookie from the broadest 2-label
 *    candidate down to the full host, and keep the broadest the browser accepts.
 *    Browsers reject cookies on public suffixes (.co.uk, .com.tr, github.io),
 *    so the broadest accepted candidate IS the registrable domain — no Public
 *    Suffix List needed. Returns null on localhost / IPs / single-label hosts.
 * @param {string|null|false} value
 * @returns {string|null}
 */
export function resolveCookieDomain(value) {
  if (value === null || value === undefined || value === false ||
      value === '' || value === 'off' || value === 'false' || value === 'none') {
    return null;
  }
  if (value !== 'auto') return value; // explicit domain

  if (typeof document === 'undefined' || typeof location === 'undefined') return null;
  const hostname = (location.hostname || '').replace(/\.$/, '');
  if (!hostname || hostname === 'localhost' ||
      /^\d{1,3}(\.\d{1,3}){3}$/.test(hostname) ||  // IPv4
      hostname.indexOf(':') !== -1 ||              // IPv6
      hostname.indexOf('.') === -1) {              // single-label host
    return null;
  }

  const parts = hostname.split('.');
  const testName = '_b2m_cd_probe';
  for (let i = parts.length - 2; i >= 0; i--) {
    const candidate = '.' + parts.slice(i).join('.');
    setCookie(testName, '1', null, candidate);
    const ok = getCookie(testName) === '1';
    deleteCookie(testName, candidate);
    if (ok) return candidate;
  }
  return null;
}

/**
 * Delete a cookie. The domain must match the one used when it was set.
 */
export function deleteCookie(name, domain = null) {
  if (typeof document === 'undefined') return;
  try {
    let cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    if (domain) cookie += `; domain=${domain}`;
    document.cookie = cookie;
  } catch (e) {
    /* ignore */
  }
}
